import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ussd } from '@repletoryreach/sdk-api';
import { Stack, Card, Group, Text, Button, SimpleGrid, Badge, Title, Skeleton, CopyButton } from '@repletoryreach/sdk-ui';
import { Alert, PageHeader, StatusBadge } from '@repletoryreach/sdk-ui/data-display';
import { NumberInput, TextInput } from '@repletoryreach/sdk-ui/inputs';
import { openConfirmModal } from '@repletoryreach/sdk-ui/modals';
import { notifySuccess, notifyError } from '@repletoryreach/sdk-ui/notifications';
import { StatCard } from '../../components/StatCard';
import { formatDateTime, formatInteger, formatMinor, formatRelative } from '../../lib/format';
import { useUssdSettings, useUssdSessionsSummary, useUpdateUssdSettings } from '../../data/useUssd';
import { useT } from '../../i18n/I18nProvider';
import { ApplicationsCard } from './components/ApplicationsCard';
import { CodesCard } from './components/CodesCard';
import './ussd.css';

const MUTED = 'var(--rr-color-text-muted)';
const fail = (e: unknown, fallback: string) => notifyError(e instanceof Error ? e.message : fallback);

/**
 * USSD, the platform's side (4 October 2026).
 *
 * The switch and what it waits for first: whether an aggregator is set up
 * under Settings → Integrations, whether the five prices exist, and the
 * shared code extensions are dialled on. Then the work: applications waiting
 * for a code, the codes given out, and the last day's sessions. Approving
 * takes the set-up fee the business has had held since applying and charges
 * the rest of the month; rejecting gives the fee back and tells them why.
 */
export default function UssdPage() {
  const t = useT();
  const settings = useUssdSettings();
  const save = useUpdateUssdSettings();
  const current = settings.data?.data ?? null;

  const [shared, setShared] = useState('');
  const [terms, setTerms] = useState('');
  const [grace, setGrace] = useState<number | ''>(7);
  const [release, setRelease] = useState<number | ''>(30);
  const [maxChars, setMaxChars] = useState<number | ''>(160);
  const [timeout, setTimeoutMs] = useState<number | ''>(4000);
  const [maxTurns, setMaxTurns] = useState<number | ''>(30);

  useEffect(() => {
    if (!current) return;
    setShared(current.shared_code ?? '');
    setTerms(current.terms_version ?? '');
    setGrace(current.rental_grace_days);
    setRelease(current.release_after_days);
    setMaxChars(current.session_limits.max_chars);
    setTimeoutMs(current.session_limits.turn_timeout_ms);
    setMaxTurns(current.session_limits.max_turns);
  }, [current]);

  const on = current?.master_enabled === true;
  const missingPrices = (current?.prices ?? []).filter((p) => p.priced === false);
  const pricesUnknown = (current?.prices ?? []).some((p) => p.priced === null);

  async function setMaster(next: boolean) {
    try {
      await save.mutateAsync({ master_enabled: next });
      notifySuccess(t(next ? 'ussd.master.switchedOn' : 'ussd.master.switchedOff'));
    } catch (e) { fail(e, t('ussd.toast.failed')); }
  }

  function confirmMaster(next: boolean) {
    openConfirmModal({
      title: t(next ? 'ussd.master.confirmOnTitle' : 'ussd.master.confirmOffTitle'),
      children: <Text role="bodySmall">{t(next ? 'ussd.master.confirmOnBody' : 'ussd.master.confirmOffBody')}</Text>,
      labels: { confirm: t(next ? 'ussd.master.switchOn' : 'ussd.master.switchOff'), cancel: t('action.cancel') },
      confirmProps: next ? undefined : { color: 'red' },
      onConfirm: () => void setMaster(next)
    });
  }

  async function saveSettings() {
    const num = (v: number | '', fallback: number) => (typeof v === 'number' ? v : fallback);
    try {
      await save.mutateAsync({
        shared_code: shared.trim(),
        terms_version: terms.trim(),
        rental_grace_days: num(grace, current?.rental_grace_days ?? 7),
        release_after_days: num(release, current?.release_after_days ?? 30),
        session_limits: {
          max_chars: num(maxChars, current?.session_limits.max_chars ?? 160),
          turn_timeout_ms: num(timeout, current?.session_limits.turn_timeout_ms ?? 4000),
          max_turns: num(maxTurns, current?.session_limits.max_turns ?? 30)
        }
      });
      notifySuccess(t('ussd.settings.saved'));
    } catch (e) { fail(e, t('ussd.toast.failed')); }
  }

  return (
    <Stack gap="lg">
      <PageHeader eyebrow={t('ussd.eyebrow')} title={t('ussd.title')} description={t('ussd.description')} />

      {settings.isError && (
        <Alert color="error" role="alert">
          <Group justify="space-between" align="center" wrap="wrap">
            <Text role="bodySmall">{t('ussd.loadFailed')}</Text>
            <Button variant="outline" size="xs" onClick={() => void settings.refetch()}>{t('action.retry')}</Button>
          </Group>
        </Alert>
      )}
      {settings.isPending && <Skeleton height={140} />}

      {current && (
        <>
          {/* The master switch, and what opening needs. */}
          <Card withBorder data-testid="ussd-master">
            <Stack gap="md">
              <Group justify="space-between" align="flex-start" wrap="wrap">
                <Stack gap="xs" style={{ minWidth: 0, flex: 1 }}>
                  <Group gap="sm">
                    <Title order={3}>{t(on ? 'ussd.master.on' : 'ussd.master.off')}</Title>
                    <Badge variant="light" color={on ? 'green' : 'gray'}>{t(on ? 'ussd.master.badgeOn' : 'ussd.master.badgeOff')}</Badge>
                  </Group>
                  <Text role="bodySmall" c={MUTED}>{t(on ? 'ussd.master.onHint' : 'ussd.master.offHint')}</Text>
                  <Text role="caption" c={MUTED}>
                    {t('ussd.master.changed', { when: formatDateTime(current.updated_at), who: current.updated_by ?? t('common.none') })}
                  </Text>
                </Stack>
                {on
                  ? <Button variant="default" disabled={save.isPending} onClick={() => confirmMaster(false)}>{t('ussd.master.switchOff')}</Button>
                  : <Button disabled={save.isPending} onClick={() => confirmMaster(true)}>{t('ussd.master.switchOn')}</Button>}
              </Group>

              <Stack gap="sm">
                <Row label={t('ussd.ready.routing')}>
                  {current.routing.configured
                    ? <StatusBadge status="healthy">{t('ussd.ready.routingOn', { provider: current.routing.active_provider ?? '' })}</StatusBadge>
                    : (
                      <Group gap="xs" wrap="wrap">
                        <StatusBadge status="warning">{t('ussd.ready.routingOff')}</StatusBadge>
                        {current.routing.reason && <Text role="caption" c={MUTED}>{current.routing.reason}</Text>}
                        <Link to="/settings/integrations">{t('ussd.ready.integrations')}</Link>
                      </Group>
                    )}
                </Row>
                <Row label={t('ussd.ready.prices')}>
                  <Stack gap={4}>
                    <Group gap={4} wrap="wrap" data-testid="ussd-prices">
                      {current.prices.map((p) => (
                        <Badge key={p.sku} size="sm" variant="light" color={p.priced === true ? 'green' : p.priced === false ? 'red' : 'gray'}>
                          {p.priced === true && typeof p.amount_minor === 'number'
                            ? t('ussd.ready.pricedAt', { name: t(`ussd.sku.${p.sku}`, p.sku), amount: formatMinor(p.amount_minor, 'ZAR') })
                            : t(p.priced === false ? 'ussd.ready.notPriced' : 'ussd.ready.priceUnknown', { name: t(`ussd.sku.${p.sku}`, p.sku) })}
                        </Badge>
                      ))}
                    </Group>
                    {(missingPrices.length > 0 || pricesUnknown) && (
                      <Text role="caption" c={MUTED}>
                        {t(pricesUnknown ? 'ussd.ready.pricesUnknownHint' : 'ussd.ready.pricesHint')}{' '}
                        <Link to="/billing/sku-rates">{t('ussd.ready.skuRates')}</Link>
                      </Text>
                    )}
                  </Stack>
                </Row>
                <Row label={t('ussd.ready.callback')}>
                  <Stack gap={4}>
                    <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                      <code className="rr-ussd__callback">{current.callback_url}</code>
                      <CopyButton value={current.callback_url}>
                        {({ copied, copy }) => (
                          <Button size="xs" variant="subtle" onClick={copy}>{copied ? t('ussd.copied') : t('ussd.copy')}</Button>
                        )}
                      </CopyButton>
                    </Group>
                    <Text role="caption" c={MUTED}>{t('ussd.ready.callbackHint')}</Text>
                  </Stack>
                </Row>
              </Stack>
            </Stack>
          </Card>

          {/* The shared code, the rental rules and the session limits. */}
          <Card withBorder data-testid="ussd-settings">
            <Stack gap="md">
              <Stack gap="xs">
                <Text role="label">{t('ussd.settings.title')}</Text>
                <Text role="bodySmall" c={MUTED}>{t('ussd.settings.hint')}</Text>
              </Stack>
              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                <TextInput label={t('ussd.settings.shared')} description={t('ussd.settings.sharedHint')} placeholder="*120*1234#" value={shared} onChange={(e) => setShared(e.currentTarget.value)} />
                <TextInput label={t('ussd.settings.terms')} description={t('ussd.settings.termsHint')} value={terms} maxLength={40} onChange={(e) => setTerms(e.currentTarget.value)} />
                <NumberInput label={t('ussd.settings.grace')} description={t('ussd.settings.graceHint')} value={grace} min={1} max={60} onChange={(v) => setGrace(typeof v === 'number' ? v : '')} />
                <NumberInput label={t('ussd.settings.release')} description={t('ussd.settings.releaseHint')} value={release} min={2} max={365} onChange={(v) => setRelease(typeof v === 'number' ? v : '')} />
              </SimpleGrid>
              <Text role="label">{t('ussd.settings.limits')}</Text>
              <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
                <NumberInput label={t('ussd.settings.maxChars')} description={t('ussd.settings.maxCharsHint')} value={maxChars} min={60} max={182} onChange={(v) => setMaxChars(typeof v === 'number' ? v : '')} />
                <NumberInput label={t('ussd.settings.timeout')} description={t('ussd.settings.timeoutHint')} value={timeout} min={1000} max={8000} step={250} onChange={(v) => setTimeoutMs(typeof v === 'number' ? v : '')} />
                <NumberInput label={t('ussd.settings.maxTurns')} description={t('ussd.settings.maxTurnsHint')} value={maxTurns} min={2} max={100} onChange={(v) => setMaxTurns(typeof v === 'number' ? v : '')} />
              </SimpleGrid>
              <Group justify="flex-end">
                <Button loading={save.isPending} onClick={() => void saveSettings()}>{t('action.save')}</Button>
              </Group>
            </Stack>
          </Card>
        </>
      )}

      <ApplicationsCard sharedCode={current?.shared_code ?? ''} />
      <CodesCard />
      <SessionsCard />
    </Stack>
  );
}

/** The last day's sessions: how many, how they ended, what they were charged, and the busiest codes. */
function SessionsCard() {
  const t = useT();
  const summary = useUssdSessionsSummary();
  const s: ussd.UssdSessionsSummary | undefined = summary.data?.data;

  return (
    <Card withBorder data-testid="ussd-sessions">
      <Stack gap="md">
        <Stack gap="xs">
          <Text role="label">{t('ussd.sessions.title')}</Text>
          <Text role="bodySmall" c={MUTED}>{t('ussd.sessions.hint')}</Text>
        </Stack>
        {summary.isError && (
          <Alert color="error" role="alert">
            <Group justify="space-between" align="center" wrap="wrap">
              <Text role="bodySmall">{t('ussd.loadFailed')}</Text>
              <Button variant="outline" size="xs" onClick={() => void summary.refetch()}>{t('action.retry')}</Button>
            </Group>
          </Alert>
        )}
        <SimpleGrid cols={{ base: 2, md: 4 }} spacing="md">
          <StatCard label={t('ussd.sessions.count')} value={s ? formatInteger(s.sessions) : '–'} loading={summary.isPending} withRule />
          <StatCard label={t('ussd.sessions.people')} value={s ? formatInteger(s.people) : '–'} loading={summary.isPending} withRule />
          <StatCard label={t('ussd.sessions.turns')} value={s ? formatInteger(s.turns) : '–'} loading={summary.isPending} withRule />
          <StatCard label={t('ussd.sessions.charged')} value={s ? formatMinor(s.charged_minor, 'ZAR') : '–'} loading={summary.isPending} tone="success" withRule />
        </SimpleGrid>
        {s && (
          <Stack gap="sm">
            <Row label={t('ussd.sessions.byState')}>
              {s.by_state.length === 0 ? t('ussd.sessions.none') : (
                <Group gap={4} wrap="wrap">
                  {s.by_state.map((b) => <Badge key={b.state} size="xs" variant="light">{t('ussd.sessions.countOf', { what: t(`ussd.sessions.state.${b.state}`, b.state), count: formatInteger(b.count) })}</Badge>)}
                </Group>
              )}
            </Row>
            <Row label={t('ussd.sessions.byOutcome')}>
              {s.by_outcome.length === 0 ? t('ussd.sessions.none') : (
                <Group gap={4} wrap="wrap">
                  {s.by_outcome.map((b) => (
                    <Badge key={b.outcome ?? 'none'} size="xs" variant="light">
                      {t('ussd.sessions.countOf', { what: b.outcome ? t(`ussd.sessions.outcome.${b.outcome}`, b.outcome) : t('ussd.sessions.noOutcome'), count: formatInteger(b.count) })}
                    </Badge>
                  ))}
                </Group>
              )}
            </Row>
            <Row label={t('ussd.sessions.byCode')}>
              {s.by_code.length === 0 ? t('ussd.sessions.none') : (
                <Group gap={4} wrap="wrap">
                  {s.by_code.map((b) => <Badge key={b.code_id} size="xs" variant="outline">{t('ussd.sessions.countOf', { what: b.code ?? t('ussd.sessions.unknownCode'), count: formatInteger(b.sessions) })}</Badge>)}
                </Group>
              )}
            </Row>
            <Row label={t('ussd.sessions.lastWebhook')}>
              {s.last_webhook
                ? <span title={formatDateTime(s.last_webhook.received_at)}>{t('ussd.sessions.webhook', { provider: s.last_webhook.provider_code, when: formatRelative(s.last_webhook.received_at) })}</span>
                : t('ussd.sessions.noWebhook')}
            </Row>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Group gap="sm" align="flex-start" wrap="nowrap">
      <Text role="caption" c={MUTED} style={{ minWidth: 150 }}>{label}</Text>
      <Text role="bodySmall" as="div" style={{ minWidth: 0, flex: 1 }}>{children}</Text>
    </Group>
  );
}
